import { Box, Card, Icon, IconButton, List } from '@zillow/constellation-10';
import { IconEditOutline } from '@zillow/constellation-icons';
import DeleteReplyButton from '../DeleteReplyButton';
import SavedReplyContent from '../SavedReplyContent';
import type { SavedReply } from '../../../../../hooks/useSavedReplies';
import isDefaultSavedReply from '../../../../../utils/isDefaultSavedReply';

type SavedReplyListProps = {
  activeReplyIndex: number;
  handleDelete: (replyIndex: number) => void;
  handleEdit: (replyIndex: number) => void;
  handleSelect: (replyIndex: number) => void;
  isDeletingReply: boolean;
  savedReplies: Array<SavedReply | null>;
  shouldDisableEditDefaultReply: boolean;
};

export default function SavedReplyList({
  activeReplyIndex,
  handleDelete,
  handleEdit,
  handleSelect,
  isDeletingReply,
  savedReplies,
  shouldDisableEditDefaultReply,
}: SavedReplyListProps): JSX.Element | null {
  if (!savedReplies.length) return null;

  return (
    <List.Root appearance="none">
      {savedReplies.map((savedReply) => {
        if (!savedReply) return null;

        const { replyIndex, reply, title } = savedReply;
        const isDefault = isDefaultSavedReply(savedReply);
        const isActive = activeReplyIndex === replyIndex;

        return (
          <List.Item key={replyIndex} css={{ paddingBlock: 0 }}>
            <Card
              css={{
                '&[data-selected="true"]': {
                  backgroundColor: 'Card.Root.interactive.neutral.bg.hover',
                },
                // Desktop
                '@media (hover: hover) and (pointer: fine)': {
                  _hover: {
                    '& .edit-reply-icons': { display: 'flex' },
                  },
                },
                // Mweb
                '@media (hover: none) and (pointer: coarse)': {
                  '&[data-selected="true"] .edit-reply-icons': { display: 'flex' },
                },
              }}
              data-selected={isActive}
              dividers={false}
              elevated={false}
              interactive
              onClick={() => handleSelect(replyIndex)}
              outlined={false}
              tone="neutral"
              body={
                <Box css={{ display: 'flex', flexDirection: 'row' }}>
                  <SavedReplyContent title={title} message={reply} />

                  <Box className="edit-reply-icons" css={{ marginLeft: 'auto', whiteSpace: 'nowrap', display: 'none' }}>
                    <IconButton
                      disabled={isDefault && shouldDisableEditDefaultReply}
                      data-testid="saved-reply-edit-btn"
                      emphasis="bare"
                      shape="circle"
                      size="md"
                      title={`Edit ${title} message`}
                      tone="brand"
                      onClick={() => handleEdit(replyIndex)}
                    >
                      <Icon>
                        <IconEditOutline />
                      </Icon>
                    </IconButton>

                    {!isDefault && (
                      <DeleteReplyButton
                        isDisabled={isDeletingReply}
                        title={title}
                        replyIndex={replyIndex}
                        handleDelete={handleDelete}
                      />
                    )}
                  </Box>
                </Box>
              }
            />
          </List.Item>
        );
      })}
    </List.Root>
  );
}
